<template>
  <div class="pycad-tool-wrapper">
    <button
      type="button"
      class="pycad-tool-btn"
      :class="{
        'pycad-tool-btn--active': active,
        'pycad-tool-btn--disabled': disabled,
        'pycad-tool-btn--labeled': showCaption,
      }"
      :disabled="disabled"
      @click="onClick"
    >
      <i :class="['mdi', icon]" :style="iconStyle"></i>
      <span v-if="showCaption" class="pycad-tool-btn__caption">
        {{ caption || label }}
      </span>
    </button>
    <div v-if="!disabled" class="pycad-tool-wrapper__tip">
      <div class="pycad-tooltip-content">
        <span>{{ label }}</span>
        <kbd v-if="shortcut" class="pycad-kbd">{{ shortcut }}</kbd>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  icon: string;
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  iconSize?: number;
  /** Show a small text caption under the icon (toolbar style). */
  showCaption?: boolean;
  /** Short caption text; falls back to `label`. */
  caption?: string;
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  disabled: false,
  iconSize: 14,
  shortcut: '',
  showCaption: false,
  caption: '',
});

const emit = defineEmits<{
  (e: 'click', evt: MouseEvent): void;
}>();

const iconStyle = computed(() => ({
  fontSize: `${props.showCaption ? Math.max(props.iconSize, 17) : props.iconSize}px`,
  lineHeight: 1,
}));

function onClick(evt: MouseEvent) {
  if (props.disabled) return;
  emit('click', evt);
}
</script>

<style scoped>
.pycad-tool-wrapper {
  position: relative;
  display: inline-flex;
}

.pycad-tool-wrapper__tip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  z-index: 50;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  white-space: nowrap;
}

.pycad-tool-wrapper:hover .pycad-tool-wrapper__tip {
  opacity: 1;
}
</style>
